import { parseOrgSlugFromPathname } from '@/lib/orgPathSlug';

export interface ResolvePublicOrgSlugInput {
  orgSlugParam?: string;
  pathname?: string;
  orgProfileSlug?: string;
  orgHostPathSlug?: string;
  orgHostOrganizationSlug?: string;
  storeOrgSlug?: string;
  isSubdomainHost?: boolean;
}

/**
 * Canonical org slug for unified public org API calls.
 * Works on path-mode (`/club-academy/:slug/...`) and branded subdomain hosts.
 */
export function resolvePublicOrgSlug(input: ResolvePublicOrgSlugInput): string | undefined {
  const fromParam = input.orgSlugParam?.trim();
  if (fromParam) return fromParam;

  if (input.pathname) {
    const fromPath = parseOrgSlugFromPathname(input.pathname);
    if (fromPath) return fromPath;
  }

  if (input.isSubdomainHost && input.orgHostPathSlug) {
    return input.orgHostPathSlug;
  }

  if (input.orgProfileSlug?.trim()) {
    return input.orgProfileSlug.trim();
  }

  if (input.orgHostOrganizationSlug?.trim()) {
    return input.orgHostOrganizationSlug.trim();
  }

  if (input.storeOrgSlug?.trim()) {
    return input.storeOrgSlug.trim();
  }

  return input.orgHostPathSlug;
}

export interface UnifiedOrgPublicSiteInput {
  orgProfile?: unknown;
  orgHostOrganization?: unknown;
  isSubdomainHost?: boolean;
  subdomainLabel?: string;
  pathname?: string;
}

/** True when the visitor is on the unified org public site (path or branded subdomain). */
export function isUnifiedOrgPublicSite(input: UnifiedOrgPublicSiteInput): boolean {
  if (input.orgProfile || input.orgHostOrganization) return true;
  if (input.isSubdomainHost && input.subdomainLabel) return true;
  if (input.pathname && parseOrgSlugFromPathname(input.pathname)) return true;
  return false;
}

/** Court hire availability should use `/public/orgs/:slug/court-hire/availability` on unified org sites. */
export function shouldPreferOrgCourtHireApi(
  input: UnifiedOrgPublicSiteInput & { resolvedOrgSlug?: string }
): boolean {
  return isUnifiedOrgPublicSite(input) || Boolean(input.resolvedOrgSlug?.trim());
}
